import { useMemo } from "react";
import { Work } from "../../types/work";
import { SYSTEM_LANGUAGE_LABELS } from "../../shared/enums/shared-enums";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import { initMetadata } from "../../controllers/formController";
import { SystemEntityProofreadingStatus } from "../../shared/enums/shared-enums";
import { MRT_ColumnDef } from "material-react-table";
import { buildEditTitleForWork } from "../../common/AddEditEntityUtils";
import {
  chineseWorkTableColumns,
  indicWorkTableColumns,
  tibetanWorkTableColumns,
} from "../../common/entities/WorkColumns";

const AVAILABILITY_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.indic]: "indic_work_availability",
  [SystemLanguages.tibetan]: "tibetan_work_availability",
} as const;

const useWorkLogic = (language: string | null | undefined) => {
  const systemLanguageType = useMemo<
    Work["system_language_type"] | null
  >(() => {
    if (!Object.values(SystemLanguages).includes(language as SystemLanguages)) {
      return null;
    }
    return language as Work["system_language_type"];
  }, [language]);

  const languageBasedColumns = useMemo<MRT_ColumnDef<Work, any>[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.indic:
        return indicWorkTableColumns;
      case SystemLanguages.tibetan:
        return tibetanWorkTableColumns;
      case SystemLanguages.chinese:
        return chineseWorkTableColumns;
    }
  }, [systemLanguageType]);

  const availabilityEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return AVAILABILITY_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const keywordsEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.indic:
      case SystemLanguages.tibetan:
        return `tibetan_keywords`;
      default:
        return "";
    }
  }, [systemLanguageType]);

  const compositionalFormEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType !== SystemLanguages.chinese
      ? "work_compositional_form"
      : "";
  }, [systemLanguageType]);

  const workOriginEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
      case SystemLanguages.chinese:
        return "work_origin";
      default:
        return "";
    }
  }, [systemLanguageType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      availabilityEnum,
      keywordsEnum,
      compositionalFormEnum,
      workOriginEnum,
    ].filter(Boolean);
  }, [
    systemLanguageType,
    availabilityEnum,
    keywordsEnum,
    compositionalFormEnum,
    workOriginEnum,
  ]);

  const metadataFieldNames = useMemo<Array<keyof Work>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return ["availability", "keywords", "compositional_form"];
  }, [systemLanguageType]);

  const defaultWork = useMemo<Work>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return {
      _key: "",
      _id: "",
      _rev: "",
      published_id: "",
      name: "",
      system_language_type: systemLanguageType,
      availability: "",
      //versions: [],
      // Related to version column in works table - commented out due to slow query issues this features triggered
      keywords: [],
      compositional_form: "",
      work_origin: [],
      translation_of_indic_work_id: null,
      notes: "",
      internal_notes: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      attributes_metadata: initMetadata<Work>(metadataFieldNames),
    };
  }, [systemLanguageType, metadataFieldNames]);

  const getDisplayName = (work: Work): string => {
    const systemLanguageLabel =
      SYSTEM_LANGUAGE_LABELS[work.system_language_type];
    const prefix = systemLanguageLabel ? `${systemLanguageLabel} Work` : "Work";
    const displayName =
      (work.name || work.published_id ? `: ` : "") +
      work.published_id +
      (work.published_id && work.name ? " - " : "") +
      work.name;
    return `${prefix}${displayName}`;
  };

  const getEditTitle = (work: Work, isCreateMode: boolean): string => {
    const systemLanguageLabel = SYSTEM_LANGUAGE_LABELS[work.system_language_type];
    return buildEditTitleForWork(work.published_id, work.name, systemLanguageLabel, isCreateMode);
  };

  const languageBasedFields = useMemo<Array<keyof Work>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.indic:
        return ["availability", "keywords", "compositional_form"];
      case SystemLanguages.tibetan:
        return [
          "availability",
          "keywords",
          "work_origin",
          "translation_of_indic_work",
          "compositional_form",
        ];
      case SystemLanguages.chinese:
        return ["translation_of_indic_work", "work_origin"];
    }
  }, [systemLanguageType]);

  const showField = (field: keyof Work): boolean => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return languageBasedFields.includes(field);
  };

  const showAncillaryConnectionSection = useMemo(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType === SystemLanguages.indic;
  }, [systemLanguageType]);

  const showVersionTable = useMemo(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return true;
  }, [systemLanguageType]);

  const showWorkTranslationTable = useMemo(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType === SystemLanguages.indic;
  }, [systemLanguageType]);

  return {
    systemLanguageType,
    languageBasedColumns,
    userEnums,
    metadataFieldNames,
    defaultWork,
    getDisplayName,
    getEditTitle,
    availabilityEnum,
    keywordsEnum,
    compositionalFormEnum,
    workOriginEnum,
    showField,
    showAncillaryConnectionSection,
    showVersionTable,
    showWorkTranslationTable,
  };
};

export default useWorkLogic;
